import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { supabase } from './supabase';

const FONTS = `@import url('https://fonts.googleapis.com/css2?family=Josefin+Sans:ital,wght@0,300;0,400;0,600;0,700&family=Outfit:wght@700;800&display=swap');`;
const GOLD  = "#E8CDA9";

const iStyle = {
  width: '100%', boxSizing: 'border-box',
  background: 'rgba(255,255,255,0.04)',
  border: '1px solid rgba(255,255,255,0.1)',
  borderRadius: 10, padding: '13px 16px',
  color: '#f0ede8', fontSize: 14,
  fontFamily: "'Josefin Sans',sans-serif",
  fontWeight: 300, outline: 'none',
  transition: 'border-color 0.15s',
};

export default function Register() {
  const [searchParams] = useSearchParams();
  const unconfirmed = searchParams.get('unconfirmed') === '1';

  const [email,    setEmail]    = useState('');
  const [password, setPassword] = useState('');
  const [confirm,  setConfirm]  = useState('');
  const [showPwd,  setShowPwd]  = useState(false);
  const [loading,  setLoading]  = useState(false);
  const [error,    setError]    = useState('');
  const [done,     setDone]     = useState(unconfirmed);
  const [doneEmail, setDoneEmail] = useState('');
  const [resent,   setResent]   = useState(false);

  // Si déjà connecté et email confirmé → plans, si connecté mais non confirmé → show done screen
  useEffect(() => {
    supabase.auth.getSession().then(({ data: { session } }) => {
      if (!session?.user) return;
      if (session.user.email_confirmed_at) {
        window.location.href = '/plans';
      } else {
        setDone(true);
        setDoneEmail(session.user.email);
      }
    });
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email || !password || !confirm) { setError('Please fill in all fields.'); return; }
    if (password.length < 6) { setError('Password must be at least 6 characters.'); return; }
    if (password !== confirm) { setError('Passwords do not match.'); return; }

    setLoading(true);
    setError('');

    const { data, error: err } = await supabase.auth.signUp({
      email,
      password,
      options: { emailRedirectTo: 'https://fyltra.app/plans' },
    });

    setLoading(false);

    if (err) {
      if (err.message.toLowerCase().includes('already')) {
        setError('An account already exists with this email. Sign in instead.');
      } else {
        setError(err.message);
      }
      return;
    }

    // Si email_confirmed_at déjà set (auto-confirm activé dans Supabase)
    if (data?.user?.email_confirmed_at) {
      window.location.href = '/plans';
      return;
    }

    setDoneEmail(email);
    setDone(true);
  };

  const resendEmail = async () => {
    setResent(false);
    const targetEmail = doneEmail || email;
    if (!targetEmail) return;
    await supabase.auth.resend({ type: 'signup', email: targetEmail, options: { emailRedirectTo: 'https://fyltra.app/plans' } });
    setResent(true);
  };

  return (
    <div style={{ minHeight: '100vh', background: '#060608', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24 }}>
      <style>{`${FONTS} * { box-sizing: border-box; margin: 0; padding: 0; } input::placeholder { color: rgba(245,242,234,0.25); }`}</style>

      <div style={{ width: '100%', maxWidth: 380 }}>

        <div style={{ textAlign: 'center', marginBottom: 44 }}>
          <a href="/"><img src="/fyltra-logo-white.svg" style={{ height: 34, width: 'auto', opacity: 0.92 }} alt="Fyltra" /></a>
        </div>

        {done ? (
          <div style={{ textAlign: 'center', padding: '8px 0' }}>
            <div style={{ width: 56, height: 56, borderRadius: '50%', background: 'rgba(232,205,169,0.1)', border: '1px solid rgba(232,205,169,0.25)', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 24px', fontSize: 24 }}>✉</div>
            <div style={{ fontSize: 22, fontWeight: 700, color: '#f0ede8', fontFamily: "'Josefin Sans',sans-serif", marginBottom: 12, letterSpacing: '-0.01em' }}>
              Check your inbox
            </div>
            <div style={{ fontSize: 13, color: 'rgba(245,242,234,0.45)', lineHeight: 1.75, fontFamily: "'Josefin Sans',sans-serif", fontWeight: 300 }}>
              We sent a confirmation link to<br/>
              <span style={{ color: GOLD, fontWeight: 600 }}>{doneEmail || 'your email'}</span>.<br/>
              Click it to confirm your account,<br/>then you'll choose your plan.
            </div>
            <div style={{ marginTop: 24, fontSize: 12, color: 'rgba(255,255,255,0.2)', fontFamily: "'Josefin Sans',sans-serif" }}>
              Didn't receive it? Check your spam folder.
            </div>
            <button onClick={resendEmail} style={{ marginTop: 16, background: 'none', border: '1px solid rgba(255,255,255,0.1)', borderRadius: 8, padding: '9px 20px', color: 'rgba(232,205,169,0.6)', fontFamily: "'Josefin Sans',sans-serif", fontSize: 12, cursor: 'pointer', transition: 'all 0.15s' }}>
              {resent ? 'Sent!' : 'Resend confirmation email'}
            </button>
            <div style={{ marginTop: 28 }}>
              <a href="/" style={{ fontSize: 12, color: 'rgba(232,205,169,0.35)', fontFamily: "'Josefin Sans',sans-serif", textDecoration: 'none' }}>← Back to home</a>
            </div>
          </div>
        ) : (
          <>
            <div style={{ marginBottom: 28 }}>
              <div style={{ fontSize: 22, fontWeight: 700, color: '#f0ede8', fontFamily: "'Josefin Sans',sans-serif", marginBottom: 6, letterSpacing: '-0.01em' }}>Create your account</div>
              <div style={{ fontSize: 13, color: 'rgba(245,242,234,0.35)', fontFamily: "'Josefin Sans',sans-serif", fontWeight: 300 }}>Choose your plan after confirming your email.</div>
            </div>

            <form onSubmit={handleSubmit}>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginBottom: 20 }}>
                <input type="email" required placeholder="Email address" value={email} onChange={e => setEmail(e.target.value)} style={iStyle}
                  onFocus={e => { e.target.style.borderColor = 'rgba(232,205,169,0.35)'; }}
                  onBlur={e  => { e.target.style.borderColor = 'rgba(255,255,255,0.1)'; }} />
                <div style={{ position: 'relative' }}>
                  <input type={showPwd ? 'text' : 'password'} required placeholder="Password" minLength={6} value={password} onChange={e => setPassword(e.target.value)} style={{ ...iStyle, paddingRight: 52 }}
                    onFocus={e => { e.target.style.borderColor = 'rgba(232,205,169,0.35)'; }}
                    onBlur={e  => { e.target.style.borderColor = 'rgba(255,255,255,0.1)'; }} />
                  <button type="button" onClick={() => setShowPwd(p => !p)} style={{ position: 'absolute', right: 14, top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer', color: 'rgba(255,255,255,0.3)', fontSize: 11, fontFamily: "'Josefin Sans',sans-serif", padding: 4 }}>
                    {showPwd ? 'HIDE' : 'SHOW'}
                  </button>
                </div>
                <div style={{ position: 'relative' }}>
                  <input type={showPwd ? 'text' : 'password'} required placeholder="Confirm password" value={confirm} onChange={e => setConfirm(e.target.value)} style={{ ...iStyle, paddingRight: 32, borderColor: confirm ? (confirm === password ? 'rgba(76,175,110,0.4)' : 'rgba(255,80,80,0.35)') : 'rgba(255,255,255,0.1)' }}
                    onFocus={e => { e.target.style.borderColor = confirm !== password ? 'rgba(255,80,80,0.4)' : 'rgba(232,205,169,0.35)'; }}
                    onBlur={e  => { e.target.style.borderColor = confirm ? (confirm === password ? 'rgba(76,175,110,0.4)' : 'rgba(255,80,80,0.35)') : 'rgba(255,255,255,0.1)'; }} />
                  {confirm && <span style={{ position: 'absolute', right: 12, top: '50%', transform: 'translateY(-50%)', fontSize: 13, color: confirm === password ? '#4caf6e' : '#e05252' }}>{confirm === password ? '✓' : '✗'}</span>}
                </div>
              </div>

              {error && <div style={{ marginBottom: 14, padding: '10px 14px', background: 'rgba(255,80,80,0.08)', border: '1px solid rgba(255,80,80,0.2)', borderRadius: 8, fontSize: 12, color: '#ff8888', fontFamily: "'Josefin Sans',sans-serif", lineHeight: 1.5 }}>{error}</div>}

              <button type="submit" disabled={loading} style={{ width: '100%', padding: '14px', background: loading ? 'rgba(232,205,169,0.4)' : 'linear-gradient(135deg,#e8cda9,#c9aa82)', border: 'none', borderRadius: 10, cursor: loading ? 'not-allowed' : 'pointer', fontFamily: "'Josefin Sans',sans-serif", fontWeight: 700, fontSize: 13, color: '#1a1208', transition: 'all 0.2s', letterSpacing: '0.06em', textTransform: 'uppercase' }}>
                {loading ? '···' : 'Create my account →'}
              </button>
            </form>

            <div style={{ marginTop: 20, fontSize: 11, color: 'rgba(255,255,255,0.2)', fontFamily: "'Josefin Sans',sans-serif", textAlign: 'center', lineHeight: 1.6 }}>
              By creating an account, you agree to our{' '}
              <a href="/cgv" style={{ color: 'rgba(232,205,169,0.4)', textDecoration: 'underline', textUnderlineOffset: 2 }}>Terms</a>
              {' '}and{' '}
              <a href="/confidentialite" style={{ color: 'rgba(232,205,169,0.4)', textDecoration: 'underline', textUnderlineOffset: 2 }}>Privacy Policy</a>.
            </div>

            <div style={{ textAlign: 'center', marginTop: 24, fontSize: 12, color: 'rgba(245,242,234,0.3)', fontFamily: "'Josefin Sans',sans-serif" }}>
              Already have an account?{' '}
              <a href="/" style={{ color: GOLD, textDecoration: 'none', fontWeight: 600 }}>Sign in →</a>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
